import React, { useMemo } from "react";

import type { FinCostCenter, FinCostCenterGroup } from "../api/finTypes";
import { groupCostCenterOptions } from "../utils/costCenters";

type Props = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  costCenters: FinCostCenter[];
  groups: FinCostCenterGroup[];
  disabled?: boolean;
  /** Primeira opção (valor vazio). Omitida: sem opção vazia além do "Escolha". */
  placeholder?: string;
  /** Opções extras antes dos grupos (ex.: "Sem centro de custo" nos filtros). */
  extraOptions?: Array<{ value: string; label: string }>;
  /** Centro gravado que não está mais na lista (inativo): aparece para não sumir. */
  current?: { id: string; code: string; name: string } | null;
  className?: string;
};

/** Select de centro de custo com os centros em `<optgroup>` por grupo. */
export default function CostCenterSelect({
  id,
  value,
  onChange,
  costCenters,
  groups,
  disabled,
  placeholder = "Escolha",
  extraOptions = [],
  current,
  className = "dpSelect",
}: Props) {
  const options = useMemo(() => groupCostCenterOptions(costCenters, groups), [costCenters, groups]);
  const missing = current && !costCenters.some((c) => c.id === current.id) ? current : null;

  return (
    <select id={id} className={className} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">{placeholder}</option>
      {extraOptions.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {missing ? (
        <option value={missing.id}>
          {missing.code} — {missing.name} (inativo)
        </option>
      ) : null}
      {options.map((group) => (
        <optgroup key={group.key} label={group.label}>
          {group.options.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
